import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cacheImages } from '../lib/cacheImages';
import SiteHeader from './shared/SiteHeader';
import { usePageMeta } from '../lib/usePageMeta';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
const IMAGE_BASE = (import.meta.env.VITE_IMAGE_URL ?? '').replace(/\/$/, '');

function imageUrl(path) {
  if (!path) return null;
  const normalized = String(path).replace(/^~\//, '/');
  if (normalized.startsWith('http')) return normalized;
  if (!IMAGE_BASE) return normalized.startsWith('/') ? normalized : `/${normalized}`;
  return `${IMAGE_BASE}${normalized.startsWith('/') ? normalized : `/${normalized}`}`;
}

function linkImage(link) {
  if (link.rutaImagen) return imageUrl(link.rutaImagen);
  // Preferir asset local si existe; onError cae a remoto / imgdef3
  return `/assets/img/k_${link.ID}.png`;
}

export default function PaginasInteres() {
  usePageMeta('Páginas de interés | LABPSI');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lenguajeId = Number(searchParams.get('lenguaje') ?? 4);
  const isCogni = lenguajeId === 4;

  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    fetch(`${API_BASE}/api/links?lenguajeId=${lenguajeId}`)
      .then((r) => {
        if (!r.ok) throw new Error('error');
        return r.json();
      })
      .then((data) => {
        setGrupos(data);
        const urls = data.flatMap((g) => (g.links ?? []).map(linkImage)).filter(Boolean);
        cacheImages(urls);
      })
      .catch(() => setError('No se pudieron cargar los links.'))
      .finally(() => setLoading(false));
  }, [lenguajeId]);

  const goBack = () => navigate(`/lenguaje/${lenguajeId}`);

  return (
    <div className={`page-tipo-test page-links ${isCogni ? 'cogni' : 'pedag'}`}>
      <div className="navbar-wrapper">
        <SiteHeader lenguajeId={lenguajeId} />

        <div className="tipo-test-title-row">
          <button type="button" className="btn-back" onClick={goBack}>
            VOLVER
          </button>
          <h1 className="title-pag">
            <span className="title-page-content">Páginas de Interés</span>
          </h1>
        </div>
      </div>

      <div className="marketing links-marketing">
        {loading && <p className="links-status">Cargando...</p>}
        {error && <p className="links-status links-error">{error}</p>}

        {!loading && !error && grupos.length === 0 && (
          <p className="links-status">No hay links disponibles por el momento.</p>
        )}

        {grupos.map((grupo) => (
          <section key={grupo.ID} className="links-grupo">
            <h2 className="links-grupo-title">{grupo.Descripcion}</h2>

            <div className="links-list">
              {(grupo.links ?? []).map((link) => {
                const hasDesc = Boolean(link.Descripcion);
                return (
                  <div key={link.ID} className="links-item">
                    <img
                      className="img-circle links-item-img"
                      src={linkImage(link) || '/assets/img/imgdef3.png'}
                      alt=""
                      decoding="async"
                      data-link-id={link.ID}
                      onError={(e) => {
                        const el = e.currentTarget;
                        const step = el.dataset.fallback || '0';
                        if (step === '0') {
                          el.dataset.fallback = '1';
                          const remote = imageUrl(`/images/k_${el.dataset.linkId}.png`);
                          if (remote) {
                            el.src = remote;
                            return;
                          }
                        }
                        if (step === '1') {
                          el.dataset.fallback = '2';
                          el.src = '/assets/img/imgdef3.png';
                          return;
                        }
                        el.onerror = null;
                        el.src = '/assets/img/logo.png';
                      }}
                    />
                    <div className={`links-item-text ${hasDesc ? '' : 'links-item-text--solo'}`}>
                      <h3 className="links-item-title">
                        <a href={link.Link} target="_blank" rel="noopener noreferrer">
                          {link.Titulo}
                        </a>
                      </h3>
                      {hasDesc && <p className="links-item-desc">{link.Descripcion}</p>}
                    </div>
                  </div>
                );
              })}
            </div>

            <hr className="links-divider" />
          </section>
        ))}

        <footer className="links-footer">
          <p className="links-footer-mail">
            Mail de Contacto ·{' '}
            <a href="mailto:labpsi@mdp.edu.ar">labpsi@mdp.edu.ar</a>
          </p>
          <button
            type="button"
            className="links-footer-up"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Arriba
          </button>
        </footer>
      </div>
    </div>
  );
}
